import {
  AgentRequest,
  AgentTaskStatus,
  agentTaskStatusToJSON,
  CodeContext,
  CodePlan,
  Command,
  CommandType,
  Diff,
  DocumentInlineComment,
  IntegrationPlatform,
  RepoMetadataRequest
} from '~/protos/agents'
import { DiffReview } from '~/store/editorSlice'
import { fetchWrapper } from '~/utils/fetchWrapper'

const updateTaskStatus = async (payload: {
  uuid: string
  status: AgentTaskStatus
  ar?: AgentRequest
}) => {
  const { uuid } = payload
  const status = agentTaskStatusToJSON(payload.status)
  const path = `/api/conversation/${uuid}/task-update/${status}`
  const ar = payload.ar ? payload.ar : AgentRequest.create()
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream'
    },
    body: AgentRequest.encode(ar).finish()
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update task status. ${reason}`)
  }
}

export const approvePlan = async (payload: { uuid: string; codePlan?: CodePlan }) => {
  const { uuid, codePlan } = payload
  const ar = AgentRequest.fromPartial({ codePlan })
  return updateTaskStatus({ uuid, status: AgentTaskStatus.PLANNING_UPDATED, ar })
}

export const regeneratePlan = async (payload: { uuid: string }) => {
  const { uuid } = payload
  return updateTaskStatus({ uuid, status: AgentTaskStatus.PLANNING_REREQUESTED })
}

export const regenerateDoc = async (payload: { uuid: string; comment: DocumentInlineComment }) => {
  const { uuid, comment } = payload
  const ar = AgentRequest.fromPartial({ documentInlineComments: [comment] })
  return updateTaskStatus({ uuid, status: AgentTaskStatus.DOCUMENT_UPDATED, ar })
}

export const regenerateImage = async (payload: { uuid: string; comment: string }) => {
  const { uuid, comment } = payload
  const ar = AgentRequest.fromPartial({
    command: Command.fromPartial({
      commandType: CommandType.GENERATE_IMAGE,
      commandString: comment
    })
  })
  return updateTaskStatus({ uuid, status: AgentTaskStatus.GENERAL_TXT_FEEDBACK_PROVIDED, ar })
}

export const applyDiff = async (payload: { uuid: string; diffs: Diff[] }) => {
  const { uuid, diffs } = payload
  const ar = AgentRequest.fromPartial({ diff: diffs })
  return updateTaskStatus({ uuid, status: AgentTaskStatus.HUMAN_DIFF_UPDATED, ar })
}

export const requestChanges = async (payload: { uuid: string; diffReview: DiffReview }) => {
  const { uuid, diffReview } = payload
  const { generalDiffComment, diffComments } = diffReview
  const ar = AgentRequest.fromPartial({ diffComments, generalDiffComment })
  return updateTaskStatus({ uuid, status: AgentTaskStatus.DIFF_COMMENT_ADDED, ar })
}

export const stopTask = async (payload: { uuid: string }) => {
  await updateTaskStatus({ ...payload, status: AgentTaskStatus.STOPPED })
}

export const getFlowNames = async (codeContext: CodeContext, platform?: IntegrationPlatform) => {
  const path = `/api/conversation/flow-names`
  let data = platform == undefined ? { codeContext } : { codeContext, platform }

  const rmr = RepoMetadataRequest.fromPartial(data)
  try {
    const response = await fetchWrapper(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: RepoMetadataRequest.encode(rmr).finish()
    })
    if (!response.ok) {
      const reason = await response.text()
      throw new Error(`Failed to get flow names. ${reason}`)
    }
    const flowNames = await response.json()
    return flowNames
  } catch (err) {
    console.error('getFlowNames', err)
    return null
  }
}

export const getFlowNamesCampaign = async (codeContext: CodeContext) => {
  const path = `/api/conversation/flow-names-test-drive`
  const rmr = RepoMetadataRequest.fromPartial({ codeContext: codeContext })
  try {
    const response = await fetchWrapper(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: RepoMetadataRequest.encode(rmr).finish()
    })
    if (!response.ok) {
      const reason = await response.text()
      throw new Error(`Failed to get flow names. ${reason}`)
    }
    const flowNames = await response.json()
    return flowNames
  } catch (err) {
    console.error('getFlowNames', err)
    return null
  }
}
